import {formatISOToBasicDateFormat} from "components/dashboard/data-input-output-table/utils";
import {CellSubType} from "utils/enums";
import {MultiSelect} from "primereact/multiselect";
import React from "react";
import { TimeSpan, convertTimeSpanToString, timeSpanToSeconds } from "utils/time-utils";
import { T } from "services/lang/LanguageService";

interface IMultiValueProps {
    selectionValues: SelectionValueTypeAlias[] | undefined;
    changeHandler: (value: any) => void;
    subType?: SelectionValuesType;
    multiValueSeparator?: string;
    value: any;
    isInEditMode: boolean;
}

type SelectionValuesType = "STRING" | "LONG" | "DOUBLE" | "TIME" | "TIMESPAN";

type SelectionValueTypeAlias = string | number | TimeSpan;
type DropdownOptionType = {label: string, value: SelectionValueTypeAlias};

const normalizeTimespan = (timespan: TimeSpan): TimeSpan => {
    return {
        days: timespan.days || 0,
        hours: timespan.hours || 0,
        minutes: timespan.minutes || 0,
        seconds: timespan.seconds || 0
    };
};

const sortValues = (a: SelectionValueTypeAlias, b: SelectionValueTypeAlias, subType: SelectionValuesType): number => {
    switch (subType) {
        case CellSubType.STRING:
            return (a as string).localeCompare(b as string);
        case CellSubType.LONG:
        case CellSubType.DOUBLE:
            return (a as number) - (b as number);
        case CellSubType.TIMESPAN:
            return timeSpanToSeconds(a as TimeSpan) - timeSpanToSeconds(b as TimeSpan);
        case CellSubType.TIME:
            return new Date(a as string).getTime() - new Date(b as string).getTime();
        default:
            return 0;
    }
};

const formatValue = (value: SelectionValueTypeAlias, subType: SelectionValuesType): string => {
    switch (subType) {
        case CellSubType.STRING:
        case CellSubType.LONG:
        case CellSubType.DOUBLE:
            return value.toString();
        case CellSubType.TIMESPAN:
            return convertTimeSpanToString(value as TimeSpan);
        case CellSubType.TIME:
            return formatISOToBasicDateFormat(value as string);
        default:
            return "";
    }
};

const generateDropdownOptions = (selectionValues: SelectionValueTypeAlias[] | undefined, subType: SelectionValuesType): DropdownOptionType[] => {
    if (!selectionValues) {
        return [];
    }

    const options = selectionValues.map((value) => {
        return {label: formatValue(value, subType), value};
    });

    options.sort((a, b) => sortValues(a.value, b.value, subType));

    return options;
};

const generateValue = (value: SelectionValueTypeAlias, subType: SelectionValuesType): string => {
    return formatValue(value, subType);
};

const renderValues = (values: SelectionValueTypeAlias[], separator: string, subType: SelectionValuesType) => {
    values.sort((a, b) => sortValues(a, b, subType));

    return values.map((value: SelectionValueTypeAlias, index: number) => {
        return (
            <React.Fragment key={index}>
                <span className="white-space-normal">{generateValue(value, subType)}</span>
                {index !== values.length - 1 && (separator === "\n" ? <br/> : separator)}
            </React.Fragment>
        );
    });
};

export default function MultiValue(props: IMultiValueProps) {
    const selectionValuesSubType = props.subType ?? CellSubType.STRING;
    const separator = props.multiValueSeparator ?? ", ";
    const options = generateDropdownOptions(props.selectionValues, selectionValuesSubType);

    const selectedItems = props.value ? props.value.values.map((value: any) => {
        let option;
        switch (props.subType) {
            case CellSubType.STRING:
            case CellSubType.LONG:
            case CellSubType.DOUBLE:
            case CellSubType.TIME:
                option = options.find(o => o.value === value.toString());
                break;
            case CellSubType.TIMESPAN:
                option = options.find(o => JSON.stringify(normalizeTimespan(o.value as TimeSpan)) === JSON.stringify(normalizeTimespan(value)));
                break;
            default:
                break;
        }
        return option ? option.value : null;
    }).filter((item: any) => item !== null) : [];

    return (
        <div>
            {props.isInEditMode ?
                <MultiSelect
                    value={selectedItems}
                    options={options}
                    placeholder={T("action.select")}
                    onChange={(e) => props.changeHandler({
                        subType: selectionValuesSubType,
                        values: e.value
                    })}
                    style={{width: "100%"}}
                    filter
                    emptyFilterMessage={T("message.no-results-found")}
                />
                :
                <div>
                    {props.value.values?.length > 0 ? renderValues(props.value.values, separator, selectionValuesSubType) : "-"}
                </div>
            }
        </div>
    );
}